import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Divider,
  Collapse,
  Switch,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid } from "@mui/x-data-grid";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

// Constants for recurrence patterns
const FREQUENCY_OPTIONS = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
];

const END_CONDITION_TYPES = [
  { value: "never", label: "Never" },
  { value: "count", label: "After count" },
  { value: "untilDate", label: "Until date" },
  { value: "duration", label: "Duration" },
];

const DURATION_UNITS = [
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
];

const DAYS_OF_WEEK = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
];

const getDefaultEndCondition = () => ({
  type: "never",
  count: null,
  untilDateTime: null,
  duration: { value: null, unit: "days" },
});

const getDefaultRecurrencePattern = () => ({
  isRecurring: false,
  frequency: "daily",
  interval: 1,
  specificTimes: [],
  daysOfWeek: [],
  dayOfMonth: null,
  startDateTime: "",
  endCondition: getDefaultEndCondition(),
});

const CarePlanWizard = ({
  open,
  onClose,
  onSubmit,
  carePlansData = [],
  patientId,
  patientName,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [carePlans, setCarePlans] = useState([]);
  const [descriptionDialog, setDescriptionDialog] = useState({ open: false, text: "" });

  useEffect(() => {
    if (open) {
      setCarePlans(Array.isArray(carePlansData) ? [...carePlansData] : []);
      setActiveStep(0);
    }
  }, [open, carePlansData]);

  // Get steps based on care plans
  const getSteps = () => {
    const steps = [];
    const typeOrder = ["clinical-notes", "monitoring", "medication", "test", "diet", "discharge", "task-list"];
    
    typeOrder.forEach((type) => {
      const plan = carePlans.find((cp) => cp.type === type);
      if (plan) {
        steps.push({ type, label: getStepLabel(type), plan });
      }
    });
    
    return steps;
  };

  const getStepLabel = (type) => {
    const labels = {
      "clinical-notes": "Clinical Notes",
      monitoring: "Monitoring",
      medication: "Medication",
      test: "Tests",
      diet: "Diet",
      discharge: "Discharge",
      "task-list": "Task List",
    };
    return labels[type] || type;
  };

  const steps = getSteps();

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleUpdateCarePlan = (type, updatedData) => {
    setCarePlans((prev) =>
      prev.map((cp) => (cp.type === type ? { ...cp, data: updatedData } : cp))
    );
  };

  const handleDeleteCarePlan = (type) => {
    setCarePlans((prev) => prev.filter((cp) => cp.type !== type));
    if (activeStep >= steps.length - 1) {
      setActiveStep(Math.max(0, activeStep - 1));
    }
  };

  const handleAddItem = (type, newItem) => {
    const plan = carePlans.find((cp) => cp.type === type);
    if (!plan) return;

    const updatedData = { ...plan.data };
    if (type === "medication") {
      updatedData.medications = [...(updatedData.medications || []), newItem];
    } else if (type === "test") {
      updatedData.tests = [...(updatedData.tests || []), newItem];
    } else if (type === "monitoring") {
      updatedData.monitoringItems = [...(updatedData.monitoringItems || []), newItem];
    }
    handleUpdateCarePlan(type, updatedData);
  };

  const handleDeleteItem = (type, itemId) => {
    const plan = carePlans.find((cp) => cp.type === type);
    if (!plan) return;

    const updatedData = { ...plan.data };
    if (type === "medication") {
      updatedData.medications = (updatedData.medications || []).filter((m) => m.id !== itemId);
    } else if (type === "test") {
      updatedData.tests = (updatedData.tests || []).filter((t) => t.id !== itemId);
    } else if (type === "monitoring") {
      updatedData.monitoringItems = (updatedData.monitoringItems || []).filter((m) => m.id !== itemId);
    }
    handleUpdateCarePlan(type, updatedData);
  };

  const handleUpdateItem = (type, itemId, updatedItem) => {
    const plan = carePlans.find((cp) => cp.type === type);
    if (!plan) return;

    const updatedData = { ...plan.data };
    if (type === "medication") {
      updatedData.medications = (updatedData.medications || []).map((m) =>
        m.id === itemId ? { ...m, ...updatedItem } : m
      );
    } else if (type === "test") {
      updatedData.tests = (updatedData.tests || []).map((t) =>
        t.id === itemId ? { ...t, ...updatedItem } : t
      );
    } else if (type === "monitoring") {
      updatedData.monitoringItems = (updatedData.monitoringItems || []).map((m) =>
        m.id === itemId ? { ...m, ...updatedItem } : m
      );
    }
    handleUpdateCarePlan(type, updatedData);
  };

  const handleUpdateTasks = (tasks) => {
    const plan = carePlans.find((cp) => cp.type === "task-list");
    if (plan) {
      handleUpdateCarePlan("task-list", { tasks });
    }
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(carePlans);
    }
    onClose();
  };

  const renderStepContent = (step) => {
    const { type, plan } = step;
    const data = plan?.data || {};

    switch (type) {
      case "clinical-notes":
        return (
          <ClinicalNotesStep
            data={data}
            onUpdate={(updated) => handleUpdateCarePlan(type, updated)}
            onDelete={() => handleDeleteCarePlan(type)}
          />
        );
      case "monitoring":
        return (
          <MonitoringStep
            data={data}
            onUpdate={(updated) => handleUpdateCarePlan(type, updated)}
            onDelete={() => handleDeleteCarePlan(type)}
            onAddItem={(item) => handleAddItem(type, item)}
            onDeleteItem={(id) => handleDeleteItem(type, id)}
            onUpdateItem={(id, item) => handleUpdateItem(type, id, item)}
          />
        );
      case "medication":
        return (
          <MedicationStep
            data={data}
            onUpdate={(updated) => handleUpdateCarePlan(type, updated)}
            onDelete={() => handleDeleteCarePlan(type)}
            onAddItem={(item) => handleAddItem(type, item)}
            onDeleteItem={(id) => handleDeleteItem(type, id)}
            onUpdateItem={(id, item) => handleUpdateItem(type, id, item)}
          />
        );
      case "test":
        return (
          <TestStep
            data={data}
            onUpdate={(updated) => handleUpdateCarePlan(type, updated)}
            onDelete={() => handleDeleteCarePlan(type)}
            onAddItem={(item) => handleAddItem(type, item)}
            onDeleteItem={(id) => handleDeleteItem(type, id)}
            onUpdateItem={(id, item) => handleUpdateItem(type, id, item)}
          />
        );
      case "diet":
        return (
          <DietStep
            data={data}
            onUpdate={(updated) => handleUpdateCarePlan(type, updated)}
            onDelete={() => handleDeleteCarePlan(type)}
          />
        );
      case "discharge":
        return (
          <DischargeStep
            data={data}
            onUpdate={(updated) => handleUpdateCarePlan(type, updated)}
            onDelete={() => handleDeleteCarePlan(type)}
          />
        );
      case "task-list":
        return (
          <TaskListStep
            tasks={data.tasks || []}
            onUpdate={handleUpdateTasks}
            patientId={patientId}
            patientName={patientName}
            onDescriptionClick={(text) => setDescriptionDialog({ open: true, text })}
          />
        );
      default:
        return <Typography>Unknown step type: {type}</Typography>;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
          Review Care Plans {patientName ? `– ${patientName}` : ""}
          <IconButton onClick={onClose} sx={{ ml: "auto" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.type}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>{renderStepContent(step)}</Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleBack} disabled={activeStep === 0}>
            Back
          </Button>
          <Box sx={{ flex: 1 }} />
          {activeStep < steps.length - 1 ? (
            <Button onClick={handleNext} variant="contained">
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Submit All
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Description Dialog */}
      <Dialog
        open={descriptionDialog.open}
        onClose={() => setDescriptionDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Description</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {descriptionDialog.text || "No description."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDescriptionDialog((prev) => ({ ...prev, open: false }))}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Step Components
const ClinicalNotesStep = ({ data, onUpdate, onDelete }) => {
  const [formData, setFormData] = useState(data);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleChange = (field) => (e) => {
    const updated = { ...formData, [field]: e.target.value };
    setFormData(updated);
    onUpdate(updated);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Clinical Notes
        </Typography>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={onDelete}
        >
          Delete
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Condition"
          value={formData.condition || ""}
          onChange={handleChange("condition")}
        />
        <TextField
          fullWidth
          size="small"
          label="Goals"
          multiline
          rows={2}
          value={formData.goals || ""}
          onChange={handleChange("goals")}
        />
        <TextField
          fullWidth
          size="small"
          label="Notes"
          multiline
          rows={3}
          value={formData.notes || ""}
          onChange={handleChange("notes")}
        />
      </Box>
    </Box>
  );
};

const MonitoringStep = ({ data, onUpdate, onDelete, onAddItem, onDeleteItem, onUpdateItem }) => {
  const [items, setItems] = useState(data.monitoringItems || []);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    parameter: "",
    frequency: "",
    thresholds: "",
    responsibleRole: "",
    duration: "",
  });

  useEffect(() => {
    setItems(data.monitoringItems || []);
  }, [data]);

  const handleAdd = (e) => {
    e.preventDefault();
    if (!formData.parameter || !formData.frequency) return;
    const newItem = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      ...formData,
      addToTaskList: false,
    };
    const updated = [...items, newItem];
    setItems(updated);
    onUpdate({ monitoringItems: updated });
    setFormData({ parameter: "", frequency: "", thresholds: "", responsibleRole: "", duration: "" });
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!formData.parameter || !formData.frequency) return;
    const updated = items.map((i) => (i.id === editingId ? { ...i, ...formData } : i));
    setItems(updated);
    onUpdate({ monitoringItems: updated });
    setEditingId(null);
    setFormData({ parameter: "", frequency: "", thresholds: "", responsibleRole: "", duration: "" });
  };

  const handleRemove = (id) => {
    const updated = items.filter((i) => i.id !== id);
    setItems(updated);
    onUpdate({ monitoringItems: updated });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Monitoring ({items.length})
        </Typography>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={onDelete}
        >
          Delete Section
        </Button>
      </Box>
      <form onSubmit={editingId ? handleUpdate : handleAdd}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Parameter *"
              value={formData.parameter}
              onChange={(e) => setFormData({ ...formData, parameter: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Frequency *"
              value={formData.frequency}
              onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Thresholds"
              value={formData.thresholds}
              onChange={(e) => setFormData({ ...formData, thresholds: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Responsible Role"
              value={formData.responsibleRole}
              onChange={(e) => setFormData({ ...formData, responsibleRole: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Duration"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" variant="outlined" size="small" startIcon={<AddIcon />}>
              {editingId ? "Update" : "Add"} Item
            </Button>
            {editingId && (
              <Button
                size="small"
                onClick={() => {
                  setEditingId(null);
                  setFormData({ parameter: "", frequency: "", thresholds: "", responsibleRole: "", duration: "" });
                }}
                sx={{ ml: 1 }}
              >
                Cancel
              </Button>
            )}
          </Grid>
        </Grid>
      </form>
      <Divider sx={{ my: 2 }} />
      {items.map((item) => (
        <Paper key={item.id} sx={{ p: 2, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.parameter}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.frequency}
              {item.thresholds && ` • ${item.thresholds}`}
              {item.responsibleRole && ` • ${item.responsibleRole}`}
              {item.duration && ` • Duration: ${item.duration}`}
            </Typography>
          </Box>
          <Box>
            <IconButton size="small" onClick={() => handleEdit(item)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleRemove(item.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

const MedicationStep = ({ data, onUpdate, onDelete, onAddItem, onDeleteItem, onUpdateItem }) => {
  const [items, setItems] = useState(data.medications || []);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    drugName: "",
    dose: "",
    route: "",
    frequency: "",
    duration: "",
    indication: "",
    prnInstructions: "",
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    setItems(data.medications || []);
  }, [data]);

  const handleAdd = (e) => {
    e.preventDefault();
    if (!formData.drugName || !formData.dose || !formData.route || !formData.frequency) return;
    const newItem = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      ...formData,
      status: "active",
      addToTaskList: false,
    };
    const updated = [...items, newItem];
    setItems(updated);
    onUpdate({ medications: updated });
    setFormData({
      drugName: "",
      dose: "",
      route: "",
      frequency: "",
      duration: "",
      indication: "",
      prnInstructions: "",
      startDate: "",
      endDate: "",
    });
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!formData.drugName || !formData.dose || !formData.route || !formData.frequency) return;
    const updated = items.map((i) => (i.id === editingId ? { ...i, ...formData } : i));
    setItems(updated);
    onUpdate({ medications: updated });
    setEditingId(null);
    setFormData({
      drugName: "",
      dose: "",
      route: "",
      frequency: "",
      duration: "",
      indication: "",
      prnInstructions: "",
      startDate: "",
      endDate: "",
    });
  };

  const handleRemove = (id) => {
    const updated = items.filter((i) => i.id !== id);
    setItems(updated);
    onUpdate({ medications: updated });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Medications ({items.length})
        </Typography>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={onDelete}
        >
          Delete Section
        </Button>
      </Box>
      <form onSubmit={editingId ? handleUpdate : handleAdd}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Drug Name *"
              value={formData.drugName}
              onChange={(e) => setFormData({ ...formData, drugName: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Dose *"
              value={formData.dose}
              onChange={(e) => setFormData({ ...formData, dose: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Route *"
              value={formData.route}
              onChange={(e) => setFormData({ ...formData, route: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Frequency *"
              value={formData.frequency}
              onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Duration (Optional)"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Indication (Optional)"
              value={formData.indication}
              onChange={(e) => setFormData({ ...formData, indication: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="PRN Instructions (Optional)"
              multiline
              rows={2}
              value={formData.prnInstructions}
              onChange={(e) => setFormData({ ...formData, prnInstructions: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date (Optional)"
              InputLabelProps={{ shrink: true }}
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date (Optional)"
              InputLabelProps={{ shrink: true }}
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" variant="outlined" size="small" startIcon={<AddIcon />}>
              {editingId ? "Update" : "Add"} Medication
            </Button>
            {editingId && (
              <Button
                size="small"
                onClick={() => {
                  setEditingId(null);
                  setFormData({
                    drugName: "",
                    dose: "",
                    route: "",
                    frequency: "",
                    duration: "",
                    indication: "",
                    prnInstructions: "",
                    startDate: "",
                    endDate: "",
                  });
                }}
                sx={{ ml: 1 }}
              >
                Cancel
              </Button>
            )}
          </Grid>
        </Grid>
      </form>
      <Divider sx={{ my: 2 }} />
      {items.map((item) => (
        <Paper key={item.id} sx={{ p: 2, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.drugName} {item.dose}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.route} • {item.frequency}
              {item.duration && ` • Duration: ${item.duration}`}
              {item.indication && ` • Indication: ${item.indication}`}
              {item.startDate && ` • Start: ${item.startDate}`}
              {item.endDate && ` • End: ${item.endDate}`}
            </Typography>
          </Box>
          <Box>
            <IconButton size="small" onClick={() => handleEdit(item)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleRemove(item.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

const TestStep = ({ data, onUpdate, onDelete, onAddItem, onDeleteItem, onUpdateItem }) => {
  const [items, setItems] = useState(data.tests || []);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    testName: "",
    priority: "routine",
    clinicalIndication: "",
    frequency: "",
    scheduledDateTime: "",
  });

  useEffect(() => {
    setItems(data.tests || []);
  }, [data]);

  const handleAdd = (e) => {
    e.preventDefault();
    if (!formData.testName || !formData.priority) return;
    const newItem = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      ...formData,
      addToTaskList: false,
    };
    const updated = [...items, newItem];
    setItems(updated);
    onUpdate({ tests: updated });
    setFormData({ testName: "", priority: "routine", clinicalIndication: "", frequency: "", scheduledDateTime: "" });
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!formData.testName || !formData.priority) return;
    const updated = items.map((i) => (i.id === editingId ? { ...i, ...formData } : i));
    setItems(updated);
    onUpdate({ tests: updated });
    setEditingId(null);
    setFormData({ testName: "", priority: "routine", clinicalIndication: "", frequency: "", scheduledDateTime: "" });
  };

  const handleRemove = (id) => {
    const updated = items.filter((i) => i.id !== id);
    setItems(updated);
    onUpdate({ tests: updated });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Tests ({items.length})
        </Typography>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={onDelete}
        >
          Delete Section
        </Button>
      </Box>
      <form onSubmit={editingId ? handleUpdate : handleAdd}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Test Name *"
              value={formData.testName}
              onChange={(e) => setFormData({ ...formData, testName: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority *</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                label="Priority *"
              >
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="routine">Routine</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Clinical Indication (Optional)"
              value={formData.clinicalIndication}
              onChange={(e) => setFormData({ ...formData, clinicalIndication: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Frequency (Optional)"
              value={formData.frequency}
              onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Scheduled Date/Time (Optional)"
              InputLabelProps={{ shrink: true }}
              value={formData.scheduledDateTime}
              onChange={(e) => setFormData({ ...formData, scheduledDateTime: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" variant="outlined" size="small" startIcon={<AddIcon />}>
              {editingId ? "Update" : "Add"} Test
            </Button>
            {editingId && (
              <Button
                size="small"
                onClick={() => {
                  setEditingId(null);
                  setFormData({ testName: "", priority: "routine", clinicalIndication: "", frequency: "", scheduledDateTime: "" });
                }}
                sx={{ ml: 1 }}
              >
                Cancel
              </Button>
            )}
          </Grid>
        </Grid>
      </form>
      <Divider sx={{ my: 2 }} />
      {items.map((item) => (
        <Paper key={item.id} sx={{ p: 2, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.testName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Priority: {item.priority}
              {item.clinicalIndication && ` • ${item.clinicalIndication}`}
              {item.frequency && ` • Frequency: ${item.frequency}`}
              {item.scheduledDateTime && ` • Scheduled: ${item.scheduledDateTime}`}
            </Typography>
          </Box>
          <Box>
            <IconButton size="small" onClick={() => handleEdit(item)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleRemove(item.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

const DietStep = ({ data, onUpdate, onDelete }) => {
  const [formData, setFormData] = useState(data);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleChange = (field) => (e) => {
    const updated = { ...formData, [field]: e.target.value };
    setFormData(updated);
    onUpdate(updated);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Diet
        </Typography>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={onDelete}
        >
          Delete
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Diet Type"
          value={formData.dietType || ""}
          onChange={handleChange("dietType")}
        />
        <TextField
          fullWidth
          size="small"
          label="Restrictions"
          multiline
          rows={2}
          value={formData.restrictions || ""}
          onChange={handleChange("restrictions")}
        />
        <TextField
          fullWidth
          size="small"
          label="Feeding Mode"
          value={formData.feedingMode || ""}
          onChange={handleChange("feedingMode")}
        />
        <TextField
          fullWidth
          size="small"
          label="Dietician Referral"
          value={formData.dieticianReferral || ""}
          onChange={handleChange("dieticianReferral")}
        />
      </Box>
    </Box>
  );
};

const DischargeStep = ({ data, onUpdate, onDelete }) => {
  const [formData, setFormData] = useState(data);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleChange = (field) => (e) => {
    const updated = { ...formData, [field]: e.target.value };
    setFormData(updated);
    onUpdate(updated);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Discharge
        </Typography>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={onDelete}
        >
          Delete
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Discharge Plan"
          multiline
          rows={3}
          value={formData.dischargePlan || ""}
          onChange={handleChange("dischargePlan")}
        />
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Tentative Date"
          InputLabelProps={{ shrink: true }}
          value={formData.tentativeDate || ""}
          onChange={handleChange("tentativeDate")}
        />
        <TextField
          fullWidth
          size="small"
          label="Follow Up Instructions"
          multiline
          rows={2}
          value={formData.followUpInstructions || ""}
          onChange={handleChange("followUpInstructions")}
        />
        <TextField
          fullWidth
          size="small"
          label="Home Care Notes"
          multiline
          rows={2}
          value={formData.homeCareNotes || ""}
          onChange={handleChange("homeCareNotes")}
        />
      </Box>
    </Box>
  );
};

const TaskListStep = ({ tasks, onUpdate, onDescriptionClick }) => {
  const [localTasks, setLocalTasks] = useState(tasks);
  const [formData, setFormData] = useState({
    taskTitle: "",
    description: "",
    priority: "Medium",
    dueDate: "",
    dueTime: "",
    assignedTo: "",
    category: "",
    recurrencePattern: {
      isRecurring: false,
      frequency: "daily",
      interval: 1,
      specificTimes: [],
      daysOfWeek: [],
      dayOfMonth: null,
      startDateTime: "",
      endCondition: {
        type: "never",
        count: null,
        untilDateTime: null,
        duration: { value: null, unit: "days" },
      },
    },
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  const getDefaultTask = () => ({
    taskTitle: "",
    description: "",
    priority: "Medium",
    dueDate: "",
    dueTime: "",
    assignedTo: "",
    category: "",
    recurrencePattern: getDefaultRecurrencePattern(),
  });

  // Parse "06:00, 12:00" to ["06:00", "12:00"]; serialize back for input
  const parseSpecificTimes = (str) =>
    (str || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  const formatSpecificTimes = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");

  const buildStartDateTime = () => {
    const { dueDate, dueTime, recurrencePattern } = formData;
    if (recurrencePattern?.startDateTime) return recurrencePattern.startDateTime;
    if (dueDate && dueTime) return `${dueDate}T${dueTime}:00Z`;
    if (dueDate) return `${dueDate}T00:00:00Z`;
    return new Date().toISOString().slice(0, 19) + "Z";
  };

  const handleRecurrenceChange = (path, value) => {
    setFormData((prev) => {
      const rp = { ...(prev.recurrencePattern || getDefaultRecurrencePattern()) };
      if (path === "isRecurring") rp.isRecurring = value;
      else if (path === "frequency") rp.frequency = value;
      else if (path === "interval") rp.interval = value === "" ? 1 : Math.max(1, parseInt(value, 10) || 1);
      else if (path === "specificTimes") rp.specificTimes = value;
      else if (path === "daysOfWeek") rp.daysOfWeek = value;
      else if (path === "dayOfMonth") rp.dayOfMonth = value === "" || value === null ? null : parseInt(value, 10);
      else if (path === "startDateTime") rp.startDateTime = value;
      else if (path.startsWith("endCondition.")) {
        const key = path.replace("endCondition.", "");
        rp.endCondition = { ...(rp.endCondition || getDefaultEndCondition()) };
        if (key === "type") rp.endCondition.type = value;
        else if (key === "count") rp.endCondition.count = value === "" ? null : parseInt(value, 10);
        else if (key === "untilDateTime") rp.endCondition.untilDateTime = value || null;
        else if (key === "duration.value") rp.endCondition.duration = { ...rp.endCondition.duration, value: value === "" ? null : parseInt(value, 10) };
        else if (key === "duration.unit") rp.endCondition.duration = { ...rp.endCondition.duration, unit: value };
      }
      return { ...prev, recurrencePattern: rp };
    });
  };

  const toggleDayOfWeek = (day) => {
    setFormData((prev) => {
      const rp = { ...(prev.recurrencePattern || getDefaultRecurrencePattern()) };
      const days = rp.daysOfWeek || [];
      const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
      rp.daysOfWeek = next;
      return { ...prev, recurrencePattern: rp };
    });
  };

  const handleAddTask = (e) => {
    e.preventDefault();
    if (!formData.taskTitle?.trim()) return;
    
    const rp = formData.recurrencePattern || getDefaultRecurrencePattern();
    const recurrencePattern = rp.isRecurring
      ? {
          isRecurring: true,
          frequency: rp.frequency || "daily",
          interval: rp.interval ?? 1,
          specificTimes: Array.isArray(rp.specificTimes) ? rp.specificTimes : [],
          daysOfWeek: Array.isArray(rp.daysOfWeek) ? rp.daysOfWeek : [],
          dayOfMonth: rp.dayOfMonth ?? null,
          startDateTime: rp.startDateTime || buildStartDateTime(),
          endCondition: {
            type: rp.endCondition?.type || "never",
            count: rp.endCondition?.type === "count" ? rp.endCondition?.count ?? null : null,
            untilDateTime: rp.endCondition?.type === "untilDate" ? rp.endCondition?.untilDateTime ?? null : null,
            duration:
              rp.endCondition?.type === "duration" && rp.endCondition?.duration
                ? { value: rp.endCondition.duration.value ?? null, unit: rp.endCondition.duration.unit || "days" }
                : { value: null, unit: "days" },
          },
        }
      : { ...getDefaultRecurrencePattern(), isRecurring: false };
    
    const newTask = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      taskTitle: formData.taskTitle.trim(),
      description: formData.description?.trim() || "",
      priority: formData.priority,
      dueDate: formData.dueDate,
      dueTime: formData.dueTime,
      assignedTo: formData.assignedTo,
      category: formData.category,
      status: "Pending",
      createdAt: new Date().toISOString(),
      recurrencePattern,
    };
    
    const updated = [...localTasks, newTask];
    setLocalTasks(updated);
    onUpdate(updated);
    setFormData(getDefaultTask());
  };

  const handleEditTask = (task) => {
    const rp = task.recurrencePattern || {};
    setFormData({
      taskTitle: task.taskTitle || "",
      description: task.description || "",
      priority: task.priority || "Medium",
      dueDate: task.dueDate || "",
      dueTime: task.dueTime || "",
      assignedTo: task.assignedTo || "",
      category: task.category || "",
      recurrencePattern: {
        ...getDefaultRecurrencePattern(),
        ...rp,
        isRecurring: Boolean(rp.isRecurring),
        endCondition: { ...getDefaultEndCondition(), ...(rp.endCondition || {}) },
      },
    });
    setEditingId(task.id);
  };

  const handleUpdateTask = (e) => {
    e.preventDefault();
    if (!formData.taskTitle?.trim()) return;
    
    const rp = formData.recurrencePattern || getDefaultRecurrencePattern();
    const recurrencePattern = rp.isRecurring
      ? {
          isRecurring: true,
          frequency: rp.frequency || "daily",
          interval: rp.interval ?? 1,
          specificTimes: Array.isArray(rp.specificTimes) ? rp.specificTimes : [],
          daysOfWeek: Array.isArray(rp.daysOfWeek) ? rp.daysOfWeek : [],
          dayOfMonth: rp.dayOfMonth ?? null,
          startDateTime: rp.startDateTime || buildStartDateTime(),
          endCondition: {
            type: rp.endCondition?.type || "never",
            count: rp.endCondition?.type === "count" ? rp.endCondition?.count ?? null : null,
            untilDateTime: rp.endCondition?.type === "untilDate" ? rp.endCondition?.untilDateTime ?? null : null,
            duration:
              rp.endCondition?.type === "duration" && rp.endCondition?.duration
                ? { value: rp.endCondition.duration.value ?? null, unit: rp.endCondition.duration.unit || "days" }
                : { value: null, unit: "days" },
          },
        }
      : { ...getDefaultRecurrencePattern(), isRecurring: false };
    
    const updated = localTasks.map((t) =>
      t.id === editingId
        ? {
            ...t,
            taskTitle: formData.taskTitle.trim(),
            description: formData.description?.trim() || "",
            priority: formData.priority,
            dueDate: formData.dueDate,
            dueTime: formData.dueTime,
            assignedTo: formData.assignedTo,
            category: formData.category,
            recurrencePattern,
          }
        : t
    );
    setLocalTasks(updated);
    onUpdate(updated);
    setFormData(getDefaultTask());
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setFormData(getDefaultTask());
    setEditingId(null);
  };

  const handleToggleStatus = (taskId) => {
    setLocalTasks((prev) => {
      const updated = prev.map((t) =>
        t.id === taskId
          ? {
              ...t,
              status: t.status === "Done" ? "Pending" : "Done",
            }
          : t
      );
      onUpdate(updated);
      return updated;
    });
  };

  const handleDeleteTask = (taskId) => {
    setLocalTasks((prev) => {
      const updated = prev.filter((t) => t.id !== taskId);
      onUpdate(updated);
      return updated;
    });
  };

  const priorityColor = (p) =>
    p === "High" ? "error" : p === "Medium" ? "warning" : "default";

  const formatRecurrenceLabel = (rp) => {
    if (!rp || !rp.isRecurring) return "";
    try {
      const { frequency, interval, specificTimes, daysOfWeek, dayOfMonth, endCondition } = rp || {};
      const intervalStr = interval > 1 ? `every ${interval} ` : "";
      let freqStr = "";
      if (frequency === "hourly") freqStr = `${intervalStr}hour(s)`;
      else if (frequency === "daily") {
        const times = Array.isArray(specificTimes) ? specificTimes.join(", ") : "";
        freqStr = times ? `daily at ${times}` : `${intervalStr}day(s)`;
      } else if (frequency === "weekly") {
        const days = Array.isArray(daysOfWeek) && daysOfWeek.length ? daysOfWeek.join(", ") : "week";
        freqStr = `${intervalStr}week(s)${days !== "week" ? ` (${days})` : ""}`;
      } else if (frequency === "monthly") {
        freqStr = dayOfMonth ? `monthly on day ${dayOfMonth}` : `${intervalStr}month(s)`;
      } else freqStr = `${intervalStr}${frequency || ""}`;
      let endStr = "";
      if (endCondition?.type === "count" && endCondition?.count != null) endStr = `, ${endCondition.count} times`;
      else if (endCondition?.type === "untilDate" && endCondition?.untilDateTime) endStr = ` until ${String(endCondition.untilDateTime).slice(0, 10)}`;
      else if (endCondition?.type === "duration" && endCondition?.duration?.value) endStr = ` for ${endCondition.duration.value} ${endCondition.duration?.unit || "days"}`;
      return `Repeats ${freqStr}${endStr}`;
    } catch {
      return "";
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Task List ({localTasks.length})
      </Typography>
      
      {/* Add/Edit Task Form */}
      <form onSubmit={editingId ? handleUpdateTask : handleAddTask}>
        <Box sx={{ mb: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            {editingId ? "Edit task" : "Add task"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Task title *"
                required
                value={formData.taskTitle}
                onChange={handleChange("taskTitle")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description (optional)"
                multiline
                rows={2}
                value={formData.description}
                onChange={handleChange("description")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  label="Priority"
                  value={formData.priority}
                  onChange={handleChange("priority")}
                >
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Due date"
                InputLabelProps={{ shrink: true }}
                value={formData.dueDate}
                onChange={handleChange("dueDate")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="time"
                label="Due time"
                InputLabelProps={{ shrink: true }}
                value={formData.dueTime}
                onChange={handleChange("dueTime")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Assigned to (optional)"
                value={formData.assignedTo}
                onChange={handleChange("assignedTo")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={formData.category || ""}
                  onChange={handleChange("category")}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Medicine">Medicine</MenuItem>
                  <MenuItem value="Vitals">Vitals</MenuItem>
                  <MenuItem value="Labs">Labs</MenuItem>
                  <MenuItem value="Rounds">Rounds</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.recurrencePattern?.isRecurring)}
                    onChange={(e) => handleRecurrenceChange("isRecurring", e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Recurring task</Typography>}
              />
            </Grid>
            <Collapse in={formData.recurrencePattern?.isRecurring}>
              <Grid item xs={12}>
                <Box sx={{ pl: 0, display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Frequency</InputLabel>
                      <Select
                        label="Frequency"
                        value={formData.recurrencePattern?.frequency || "daily"}
                        onChange={(e) => handleRecurrenceChange("frequency", e.target.value)}
                      >
                        {FREQUENCY_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      type="number"
                      label="Interval"
                      helperText="e.g. every 6 hours"
                      value={formData.recurrencePattern?.interval ?? 1}
                      onChange={(e) => handleRecurrenceChange("interval", e.target.value)}
                      inputProps={{ min: 1 }}
                      sx={{ width: 90 }}
                    />
                  </Box>
                  {(formData.recurrencePattern?.frequency === "daily" || formData.recurrencePattern?.frequency === "custom") && (
                    <TextField
                      size="small"
                      label="Specific times (optional)"
                      placeholder="06:00, 12:00, 18:00"
                      value={formatSpecificTimes(formData.recurrencePattern?.specificTimes)}
                      onChange={(e) => handleRecurrenceChange("specificTimes", parseSpecificTimes(e.target.value))}
                    />
                  )}
                  {formData.recurrencePattern?.frequency === "weekly" && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Days of week
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {DAYS_OF_WEEK.map((day) => (
                          <FormControlLabel
                            key={day}
                            control={
                              <Checkbox
                                size="small"
                                checked={(formData.recurrencePattern?.daysOfWeek || []).includes(day)}
                                onChange={() => toggleDayOfWeek(day)}
                              />
                            }
                            label={<Typography variant="caption">{day}</Typography>}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {formData.recurrencePattern?.frequency === "monthly" && (
                    <TextField
                      size="small"
                      type="number"
                      label="Day of month (1-31)"
                      value={formData.recurrencePattern?.dayOfMonth ?? ""}
                      onChange={(e) => handleRecurrenceChange("dayOfMonth", e.target.value)}
                      inputProps={{ min: 1, max: 31 }}
                      sx={{ width: 140 }}
                    />
                  )}
                  <TextField
                    size="small"
                    label="Start date/time (optional)"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={
                      formData.recurrencePattern?.startDateTime
                        ? formData.recurrencePattern.startDateTime.slice(0, 16)
                        : ""
                    }
                    onChange={(e) => handleRecurrenceChange("startDateTime", e.target.value ? `${e.target.value}:00Z` : "")}
                    sx={{ maxWidth: 260 }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      End condition
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          label="Type"
                          value={formData.recurrencePattern?.endCondition?.type || "never"}
                          onChange={(e) => handleRecurrenceChange("endCondition.type", e.target.value)}
                        >
                          {END_CONDITION_TYPES.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {formData.recurrencePattern?.endCondition?.type === "count" && (
                        <TextField
                          size="small"
                          type="number"
                          label="Count"
                          value={formData.recurrencePattern?.endCondition?.count ?? ""}
                          onChange={(e) => handleRecurrenceChange("endCondition.count", e.target.value)}
                          inputProps={{ min: 1 }}
                          sx={{ width: 90 }}
                        />
                      )}
                      {formData.recurrencePattern?.endCondition?.type === "untilDate" && (
                        <TextField
                          size="small"
                          type="datetime-local"
                          label="Until"
                          InputLabelProps={{ shrink: true }}
                          value={
                            formData.recurrencePattern?.endCondition?.untilDateTime
                              ? formData.recurrencePattern.endCondition.untilDateTime.slice(0, 16)
                              : ""
                          }
                          onChange={(e) =>
                            handleRecurrenceChange("endCondition.untilDateTime", e.target.value ? `${e.target.value}:00Z` : null)
                          }
                          sx={{ minWidth: 200 }}
                        />
                      )}
                      {formData.recurrencePattern?.endCondition?.type === "duration" && (
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <TextField
                            size="small"
                            type="number"
                            label="Value"
                            value={formData.recurrencePattern?.endCondition?.duration?.value ?? ""}
                            onChange={(e) => handleRecurrenceChange("endCondition.duration.value", e.target.value)}
                            inputProps={{ min: 1 }}
                            sx={{ width: 80 }}
                          />
                          <FormControl size="small" sx={{ minWidth: 90 }}>
                            <InputLabel>Unit</InputLabel>
                            <Select
                              label="Unit"
                              value={formData.recurrencePattern?.endCondition?.duration?.unit || "days"}
                              onChange={(e) => handleRecurrenceChange("endCondition.duration.unit", e.target.value)}
                            >
                              {DURATION_UNITS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Collapse>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button type="submit" variant="contained" size="small">
                  {editingId ? "Update task" : "Add task"}
                </Button>
                {editingId && (
                  <Button type="button" variant="outlined" size="small" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </form>

      <Divider sx={{ my: 2 }} />

      {localTasks.length > 0 ? (
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={localTasks.map((t) => {
              const recurrenceStr = t.recurrencePattern?.isRecurring
                ? formatRecurrenceLabel(t.recurrencePattern)
                : "";
              return {
                id: t.id,
                taskTitle: String(t.taskTitle ?? ""),
                description: String(t.description ?? ""),
                priority: t.priority === "High" || t.priority === "Medium" || t.priority === "Low" ? t.priority : "Medium",
                due: t.dueDate ? (t.dueTime ? `${t.dueDate} ${t.dueTime}` : String(t.dueDate)) : "",
                assignedTo: String(t.assignedTo ?? ""),
                status: t.status === "Done" ? "Done" : "Pending",
                recurrence: recurrenceStr,
              };
            })}
            getRowId={(row) => row.id}
            columns={[
              {
                field: "status",
                headerName: "",
                width: 48,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleStatus(params.row.id);
                    }}
                    color={params.row.status === "Done" ? "success" : "default"}
                  >
                    {params.row.status === "Done" ? (
                      <CheckCircleOutlineIcon fontSize="small" />
                    ) : (
                      <RadioButtonUncheckedIcon fontSize="small" />
                    )}
                  </IconButton>
                ),
              },
              {
                field: "taskTitle",
                headerName: "Task",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      textDecoration: params.row.status === "Done" ? "line-through" : "none",
                      opacity: params.row.status === "Done" ? 0.8 : 1,
                      py: 0.5,
                    }}
                  >
                    {params.row.taskTitle}
                  </Typography>
                ),
              },
              {
                field: "description",
                headerName: "Description",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDescriptionClick(params.row.description || "");
                      }}
                      title="View description"
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textDecoration: params.row.status === "Done" ? "line-through" : "none",
                        opacity: params.row.status === "Done" ? 0.8 : 1,
                      }}
                    >
                      {params.row.description || "—"}
                    </Typography>
                  </Box>
                ),
              },
              {
                field: "priority",
                headerName: "Priority",
                width: 100,
                renderCell: (params) => (
                  <Chip
                    size="small"
                    label={params.value || "Medium"}
                    color={priorityColor(params.value || "Medium")}
                  />
                ),
              },
              {
                field: "due",
                headerName: "Due",
                width: 180,
                valueGetter: (params) => params.row?.due ?? "",
              },
              {
                field: "recurrence",
                headerName: "Recurrence",
                width: 140,
                renderCell: (params) =>
                  params.value ? (
                    <Chip
                      size="small"
                      label={String(params.value)}
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", maxWidth: 130 }}
                    />
                  ) : null,
              },
              {
                field: "actions",
                headerName: "Action",
                width: 120,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const task = localTasks.find((t) => t.id === params.row.id);
                        if (task) handleEditTask(task);
                      }}
                      title="Edit task"
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTask(params.row.id);
                      }}
                      title="Delete task"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ),
              },
            ]}
            getRowClassName={(params) => (params.row.status === "Done" ? "task-done" : "")}
            disableRowSelectionOnClick
            hideFooter
            sx={{
              border: "none",
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid",
                borderColor: "divider",
                alignItems: "flex-start",
                paddingTop: 1,
                paddingBottom: 1,
              },
              "& .MuiDataGrid-cellContent": { overflow: "visible", whiteSpace: "normal" },
              "& .MuiDataGrid-row": { minHeight: "52px !important", maxHeight: "none !important" },
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid",
                borderColor: "divider",
                minHeight: "40px !important",
                maxHeight: "40px !important",
              },
            }}
          />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No tasks in this list.
        </Typography>
      )}
    </Box>
  );
};

export default CarePlanWizard;

